/**
 * FlowTransportPanel — compact icon-button row inside the left sidebar.
 *
 * Matches the SR editor's LeftSidebar transport section: 36×36 icon-only
 * buttons in a 4-column grid, no text labels, rich tooltips via Tooltip component.
 *
 * Buttons (single row of 4):
 *   ⚙  Settings        → opens FlowSettingsDialog
 *   🥁  Metronome      → tick on each whole-beat crossing during playback
 *   📊  Difficulty      → pattern-density gauge overlay
 *   🧩  Pattern Library → browse / insert saved note patterns
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useFlowStore } from './flowStore';
import { scheduleTickAt } from './flowSounds';
import { probe } from './flowSyncProbe';
import * as engine from './flowAudioEngine';
import FloatingWindow from './FloatingWindow';
import { Group } from './FlowGroup';
import { color } from '../../components/ui/theme';
import TransportBar from '../../components/ui/TransportBar';

// ── Transport panel ──────────────────────────────────────────────────────────

export default function FlowTransportPanel() {
  // Settings dialog state lives in the store so the top-right header button can
  // toggle it (the gear no longer lives in this transport grid).
  // settingsOpen state lives in the store; the dialog is rendered by FlowApp now.
  const setSettingsOpen       = useFlowStore(s => s.setSettingsOpen);
  const [diffOpen,      setDiffOpen]      = useState(false);
  const [patLibOpen,    setPatLibOpen]    = useState(false);

  const metronomeEnabled      = useFlowStore(s => s.metronomeEnabled);
  const setMetronomeEnabled   = useFlowStore(s => s.setMetronomeEnabled);
  const liveMapping           = useFlowStore(s => s.liveMapping);
  const setLiveMapping        = useFlowStore(s => s.setLiveMapping);
  const debugSync             = useFlowStore(s => s.debugSync);
  const setDebugSync          = useFlowStore(s => s.setDebugSync);
  const iface                 = useFlowStore(s => s.flowInterfaceSettings);
  const setIface              = useFlowStore(s => s.setFlowInterfaceSettings);

  // Metronome — LOOK-AHEAD SCHEDULER (rhythm-game standard).
  // Reactively firing a click "now" when the frame loop notices a beat crossed
  // inherits ~16ms of frame jitter + scheduling latency → audible drift. Instead
  // we run a short polling loop that SCHEDULES each upcoming beat's click at its
  // exact AudioContext time (engine.getCtxTimeForSong), so the clicks are
  // committed to the audio thread ahead of time and stay sample-accurate with
  // the music (which plays on the very same AudioContext). No syncOffset here:
  // metronome + music share the output path, so they're aligned by construction.
  useEffect(() => {
    if (!metronomeEnabled) return;
    const LOOKAHEAD = 0.12;   // seconds of beats to schedule in advance
    const POLL_MS = 25;
    let scheduledBeat = -1;   // highest integer beat index already scheduled
    let lastPos = -1;         // detect seeks / loops to re-sync

    const id = window.setInterval(() => {
      const st = useFlowStore.getState();
      if (!st.playing || !engine.isPlaying()) { scheduledBeat = -1; lastPos = -1; return; }

      const bpm = Math.max(1, st.chart.bpm);
      const offset = st.chart.offset_seconds;
      const secPerBeat = 60 / bpm;
      const pos = engine.getPosition();          // true song time (audio clock)

      // Reset if the playhead jumped (seek/loop) — anything beyond a normal tick.
      if (lastPos >= 0 && Math.abs(pos - lastPos) > POLL_MS / 1000 * st.playbackSpeed + 0.25) {
        scheduledBeat = -1;
      }
      lastPos = pos;

      const horizon = pos + LOOKAHEAD;
      let nextBeat = scheduledBeat < 0
        ? Math.max(0, Math.ceil((pos - offset) / secPerBeat - 1e-6))
        : scheduledBeat + 1;

      for (let i = 0; i < 64; i++) {            // safety bound
        const songT = offset + nextBeat * secPerBeat;
        if (songT > horizon) break;
        if (songT >= pos - 0.03) {              // skip beats already past
          const ctxTarget = engine.getCtxTimeForSong(songT);
          probe('metro', { beat: nextBeat, songT, ctxTarget, accent: nextBeat % 4 === 0 });
          scheduleTickAt(ctxTarget, nextBeat % 4 === 0);
        }
        scheduledBeat = nextBeat;
        nextBeat++;
      }
    }, POLL_MS);

    return () => window.clearInterval(id);
  }, [metronomeEnabled]);

  const closePanels = (except?: 'settings' | 'diff' | 'patlib') => {
    if (except !== 'settings') setSettingsOpen(false);
    if (except !== 'diff')     setDiffOpen(false);
    if (except !== 'patlib')   setPatLibOpen(false);
  };

  return (
    <>
      {/* ── Button grid (collapsible, like the other sidebar panels) ──── */}
      <Group label="Transport">
        {/* Editor Settings + Play on Phone live in the top-right header (FlowApp). */}
        <TransportBar
          items={[
            { id: 'metronome', active: metronomeEnabled, onClick: () => setMetronomeEnabled(!metronomeEnabled) },
            { id: 'difficulty', active: diffOpen, onClick: () => { closePanels('diff'); setDiffOpen(v => !v); } },
            { id: 'library', active: patLibOpen, onClick: () => { closePanels('patlib'); setPatLibOpen(v => !v); } },
            { id: 'record', active: liveMapping, onClick: () => setLiveMapping(!liveMapping) },
            { id: 'debug', active: debugSync, onClick: () => setDebugSync(!debugSync) },
            { id: 'waveform', active: iface.waveformVisible !== false, onClick: () => setIface({ waveformVisible: !(iface.waveformVisible !== false) }) },
          ]}
        />
      </Group>

      {/* ── Floating, draggable/resizable tool windows ─────────────────────── */}
      {/* FlowSettingsDialog now lives at the FlowApp shell level so the gear works
          even when this (left-sidebar) panel is collapsed/unmounted. */}
      {diffOpen && (
        <FloatingWindow
          id="diff"
          title="Pattern Difficulty"
          accent="#22C55E"
          defaultRect={{ x: Math.max(8, window.innerWidth - 280), y: 70, w: 250, h: 150 }}
          minH={120}
          onClose={() => setDiffOpen(false)}
        >
          <FlowDifficultyBar />
        </FloatingWindow>
      )}
      {patLibOpen && (
        <FloatingWindow
          id="patlib"
          title="Pattern Library"
          accent="#E879F9"
          defaultRect={{ x: Math.max(8, window.innerWidth - 320), y: 70, w: 280, h: 240 }}
          onClose={() => setPatLibOpen(false)}
        >
          <FlowPatternLibraryPanel />
        </FloatingWindow>
      )}
    </>
  );
}

// ── Difficulty bar ────────────────────────────────────────────────────────────
//
// Client-side note-density computation. Renders as a fixed overlay above the
// 3D viewport regardless of where the transport panel sits in the DOM.

const DIFF_THRESHOLDS = [
  { label: 'Easy',   max: 0.5,  color: '#34D399' },
  { label: 'Normal', max: 1.0,  color: '#FACC15' },
  { label: 'Hard',   max: 1.6,  color: '#F97316' },
  { label: 'Expert', max: Infinity, color: '#F87171' },
];

function FlowDifficultyBar() {
  const chart    = useFlowStore(s => s.chart);
  const duration = useFlowStore(s => s.duration);

  const { npb, label, color, pct } = useMemo(() => {
    const totalBeats = Math.max(1, (duration * chart.bpm) / 60);
    const trailCount = chart.notes.filter(n => n.duration_beats > 0).length;
    const weighted   = chart.notes.length + trailCount * 0.5;
    const npb        = weighted / totalBeats;

    const tier    = DIFF_THRESHOLDS.find(t => npb <= t.max) ?? DIFF_THRESHOLDS[3];
    const tierIdx = DIFF_THRESHOLDS.indexOf(tier);
    const lo  = tierIdx === 0 ? 0 : DIFF_THRESHOLDS[tierIdx - 1].max;
    const hi  = tier.max === Infinity ? lo + 1 : tier.max;
    const pct = Math.min(1, (tierIdx + (npb - lo) / (hi - lo)) / DIFF_THRESHOLDS.length);

    return { npb, label: tier.label, color: tier.color, pct };
  }, [chart.notes, chart.bpm, duration]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <span style={{ color, fontSize: 11, fontWeight: 700 }}>{label}</span>
      </div>
      <div style={diffTrackStyle}>
        <div style={{ ...diffFillStyle, width: `${pct * 100}%`, background: color }} />
        {[1/4, 2/4, 3/4].map((t, i) => (
          <div key={i} style={{ ...dividerStyle, left: `${t * 100}%` }} />
        ))}
      </div>
      <div style={diffStatsStyle}>
        <span>{chart.notes.length} notes</span>
        <span>{npb.toFixed(2)} n/beat</span>
        <span>{chart.notes.filter(n => n.duration_beats > 0).length} trails</span>
      </div>
    </div>
  );
}

// ── Pattern Library panel ─────────────────────────────────────────────────────
//
// Placeholder overlay — wired up as a fixed panel above the viewport.
// Full implementation (saved patterns, drag-to-insert) coming soon.

function FlowPatternLibraryPanel() {
  return (
    <div style={patLibPlaceholderStyle}>
      <span style={{ fontSize: 28, opacity: 0.4 }}>🧩</span>
      <span style={{ fontSize: 10, color: color.inkFaint, textAlign: 'center', lineHeight: 1.5 }}>
        Pattern Library<br />
        <span style={{ color: '#4A4366' }}>coming soon — save &amp; reuse note patterns</span>
      </span>
    </div>
  );
}


// ── Styles ────────────────────────────────────────────────────────────────────

// The transport button row + look now live in the shared <TransportBar>.

// Floating tool windows now use the shared <FloatingWindow> chrome (draggable,
// resizable, persisted) — see FloatingWindow.tsx.

// ── Difficulty bar internals ──────────────────────────────────────────────────

const diffTrackStyle: React.CSSProperties = {
  position: 'relative',
  height: 8,
  background: 'rgba(30, 20, 60, 0.8)',
  borderRadius: 4,
  overflow: 'hidden',
};

const diffFillStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0, left: 0,
  height: '100%',
  borderRadius: 4,
  transition: 'width 0.4s ease, background 0.4s ease',
};

const dividerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  width: 1,
  height: '100%',
  background: 'rgba(255, 255, 255, 0.15)',
};

const diffStatsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 9,
  color: color.inkFaint,
  letterSpacing: 0.3,
};

// ── Pattern Library placeholder ───────────────────────────────────────────────

const patLibPlaceholderStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  padding: '16px 8px',
};
